import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
} from "@mui/material";
import {
    Add as AddIcon,
    Search,
    Delete as DeleteIcon,
    Close,
    HelpOutline,
    CheckCircle,
} from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import {
    listEmailDomains,
    createEmailDomain,
    verifyEmailDomain,
    deleteEmailDomain,
    getEmailDomainDnsRecords,
} from "../../api/emailBusinessApi";
import SesDnsRecordsTable from "../Admin/shared/SesDnsRecordsTable";
import useNotification from "../common/Notification";

const GLASS_PAPER_SX = {
    background: "rgba(255, 255, 255, 0.6)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.85)",
    borderRadius: 2,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
};

const ROWS_PER_PAGE = 10;
const EMPTY_FORM = { domain: "", description: "" };

const normalizeStatus = (item) => {
    const status = String(item?.ses_verification_status ?? item?.status ?? "").toUpperCase();
    if (status === "SUCCESS" || status === "AUTHENTICATED") return "Verified";
    if (status === "PENDING") return "Pending";
    return "Not verified";
};

const mapDomain = (item) => ({
    id: item.id,
    domain: item.domain,
    description: item.description ?? "",
    status: normalizeStatus(item),
    ses_verification_status: item.ses_verification_status,
    user_id: item.user_id,
    created_at: item.created_at,
    updated_at: item.updated_at,
});

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: "rgba(255,255,255,0.8)",
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
            borderWidth: 2,
        },
    },
};

const DomainStatus = ({ displayStatus }) => {
    const isVerified = displayStatus === "Verified";
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            {isVerified ? (
                <CheckCircle sx={{ fontSize: 18, color: green[600] }} />
            ) : (
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: red[500],
                        flexShrink: 0,
                    }}
                />
            )}
            <Typography variant="body2" color="text.primary">
                {displayStatus}
            </Typography>
            {!isVerified && (
                <Tooltip title="Domain DNS records (DKIM/DMARC) are not verified yet.">
                    <HelpOutline sx={{ fontSize: 16, color: "text.secondary" }} />
                </Tooltip>
            )}
        </Box>
    );
};

const ManageDomains = () => {
    const notify = useNotification();
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [dnsDialog, setDnsDialog] = useState({ open: false, domain: null, records: [], loading: false });

    const fetchDomains = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await listEmailDomains({ per_page: 100 });
            setDomains(Array.isArray(rows) ? rows.map(mapDomain) : []);
        } catch (error) {
            notify(error.message || "Failed to load domains", "error");
            setDomains([]);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    const filteredDomains = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return domains;
        return domains.filter(
            (d) =>
                d.domain.toLowerCase().includes(q) ||
                d.status.toLowerCase().includes(q)
        );
    }, [query, domains]);

    const totalPages = Math.max(1, Math.ceil(filteredDomains.length / ROWS_PER_PAGE));
    const paginatedDomains = filteredDomains.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE
    );

    const openCreateDialog = () => {
        setForm(EMPTY_FORM);
        setCreateOpen(true);
    };

    const closeCreateDialog = () => {
        if (saving) return;
        setCreateOpen(false);
        setForm(EMPTY_FORM);
    };

    const handleFormChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleAddDomain = async () => {
        const domain = form.domain.trim().toLowerCase();
        if (!domain || saving) return;

        setSaving(true);
        try {
            const result = await createEmailDomain({
                domain,
                description: form.description.trim() || undefined,
            });

            setPage(1);
            notify("Domain added successfully", "success");
            setCreateOpen(false);
            setForm(EMPTY_FORM);
            await fetchDomains();
            if (result.ses?.dns_records?.length) {
                setDnsDialog({
                    open: true,
                    domain: result.data || { domain },
                    records: result.ses.dns_records,
                    loading: false,
                });
            }
        } catch (error) {
            notify(error.message || "Failed to add domain", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async (id) => {
        try {
            const result = await verifyEmailDomain(id);
            notify(result.ses?.message || result.message || "Verification checked", result.ses?.is_verified ? "success" : "warning");
            await fetchDomains();
        } catch (error) {
            notify(error.message || "Failed to verify domain", "error");
        }
    };

    const openDnsDialog = async (row) => {
        setDnsDialog({ open: true, domain: row, records: [], loading: true });
        try {
            const data = await getEmailDomainDnsRecords(row.id);
            setDnsDialog((prev) => ({ ...prev, records: data.dns_records || [], loading: false }));
        } catch (error) {
            notify(error.message || "Failed to load DNS records", "error");
            setDnsDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteEmailDomain(id);
            notify("Domain deleted successfully", "success");
            await fetchDomains();
        } catch (error) {
            notify(error.message || "Failed to delete domain", "error");
        }
    };

    return (
        <Box sx={{ mt: 2, p: 0 }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <Box sx={{ maxWidth: { xs: "100%", md: "760px" } }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            lineHeight: 1.2,
                            color: "text.primary",
                        }}
                    >
                        Domains
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        An email domain is the part of an email address that comes after the @
                        symbol. Authenticating your domain improves deliverability and trust.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Chip
                        size="small"
                        label={`Total: ${domains.length}`}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: "rgba(25,118,210,0.10)",
                            color: "text.primary",
                            border: "1px solid rgba(25,118,210,0.20)",
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                        }}
                    >
                        Add Domain
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ ...GLASS_PAPER_SX, mb: 2 }}>
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        alignItems: "center",
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search domain by name"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={fieldSx}
                    />
                    <Chip
                        size="small"
                        label={`Showing: ${filteredDomains.length}`}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.08)",
                        }}
                    />
                </Box>
            </Paper>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    ...GLASS_PAPER_SX,
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                            {[
                                { label: "Domain Name", align: "left" },
                                { label: "Domain Status", align: "left" },
                                { label: "Actions", align: "center" },
                            ].map((h) => (
                                <TableCell
                                    key={h.label}
                                    align={h.align}
                                    sx={{
                                        fontWeight: 800,
                                        color: "text.primary",
                                        bgcolor: "#f8fafc",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        py: 1.5,
                                        textTransform: "uppercase",
                                        fontSize: "0.75rem",
                                        letterSpacing: 0.4,
                                    }}
                                >
                                    {h.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : paginatedDomains.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {query
                                            ? "No domains found matching your search"
                                            : "No domains found"}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedDomains.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{
                                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                                        "& td": {
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2">{row.domain}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <DomainStatus displayStatus={row.status} />
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: 320 }}>
                                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                                            <Button size="small" variant="outlined" onClick={() => openDnsDialog(row)}>
                                                DNS
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleVerify(row.id)}
                                                disabled={row.status === "Verified"}
                                            >
                                                Verify
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="text"
                                                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => handleDelete(row.id)}
                                                sx={{ color: "error.main", fontWeight: 800 }}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredDomains.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mt: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        Showing {(page - 1) * ROWS_PER_PAGE + 1} to{" "}
                        {Math.min(page * ROWS_PER_PAGE, filteredDomains.length)} of{" "}
                        {filteredDomains.length} domains
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                        size="small"
                    />
                </Box>
            )}

            <Dialog
                open={createOpen}
                onClose={closeCreateDialog}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: "rgba(0,0,0,0.45)" },
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        backgroundColor: "background.paper",
                        boxShadow: 6,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Add Domain
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Add a domain you want to authenticate for sending emails.
                        </Typography>
                    </Box>
                    <Button
                        onClick={closeCreateDialog}
                        sx={{ minWidth: "auto", p: 0.5, alignSelf: "flex-start" }}
                        color="inherit"
                    >
                        <Close />
                    </Button>
                </DialogTitle>

                <DialogContent sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        After adding a domain, publish the DNS records shown and use Verify to confirm authentication.
                        If domain setup is unavailable, contact your platform administrator.
                    </Alert>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Domain Name{" "}
                            <Box component="span" sx={{ color: "error.main" }}>
                                *
                            </Box>
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1.25 }}
                        >
                            Enter the domain without http:// or www.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="Enter domain name"
                            value={form.domain}
                            onChange={handleFormChange("domain")}
                            sx={fieldSx}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Description
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1.25 }}
                        >
                            Optional note to identify this domain.
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            placeholder="Optional description"
                            value={form.description}
                            onChange={handleFormChange("description")}
                            sx={fieldSx}
                        />
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                        pt: 1,
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        onClick={closeCreateDialog}
                        disabled={saving}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "primary.main",
                            px: 0,
                            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddDomain}
                        disabled={!form.domain.trim() || saving}
                        startIcon={
                            saving ? <CircularProgress size={16} color="inherit" /> : null
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 3,
                        }}
                    >
                        {saving ? "Adding..." : "Add Domain"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={dnsDialog.open}
                onClose={() => setDnsDialog({ open: false, domain: null, records: [], loading: false })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>DNS records — {dnsDialog.domain?.domain}</DialogTitle>
                <DialogContent dividers>
                    {dnsDialog.loading ? (
                        <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={24} /></Box>
                    ) : (
                        <SesDnsRecordsTable
                            records={dnsDialog.records}
                            verificationStatus={dnsDialog.domain?.ses_verification_status}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDnsDialog({ open: false, domain: null, records: [], loading: false })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageDomains;
